<template>
    <Teleport to="body">
        <div
            class="toast-container toast-container--nu"
            role="region"
            :aria-label="$t('Notifications')"
            aria-live="polite"
        >
            <TransitionGroup name="toast">
                <div
                    v-for="toast in toasts"
                    :key="toast.id"
                    class="toast"
                    :class="`toast--${toast.variant}`"
                    role="status"
                >
                    <div class="toast__icon" aria-hidden="true">
                        <Icon :icon="iconFor(toast.variant)" class="size-5" />
                    </div>
                    <div class="toast__body">
                        <p v-if="toast.title" class="toast__title">{{ toast.title }}</p>
                        <p class="toast__message">{{ toast.message }}</p>
                    </div>
                    <button class="toast__close" @click="dismiss(toast.id)" :aria-label="$t('Close')">
                        <Icon icon="ri:close-line" class="size-4" />
                    </button>
                </div>
            </TransitionGroup>
        </div>
    </Teleport>
</template>

<script setup lang="ts">
import { useToast, type ToastVariant } from '@/composables/useToast';

const { toasts, dismiss } = useToast();

function iconFor(variant: ToastVariant) {
    switch (variant) {
        case 'success':
            return 'ri:checkbox-circle-fill';
        case 'error':
            return 'ri:error-warning-fill';
        case 'warning':
            return 'ri:alert-fill';
        default:
            return 'ri:information-fill';
    }
}
</script>

<style lang="scss" scoped>
.toast-container {
    position: fixed;
    top: calc(var(--navbar-height) + env(safe-area-inset-top, 0px) + 16px);
    left: 50%;
    transform: translateX(-50%);
    z-index: var(--z-toast);
    display: flex;
    flex-direction: column;
    gap: 10px;
    pointer-events: none;
    width: calc(100% - 24px);
    max-width: 420px;
}

.toast {
    pointer-events: auto;
    display: flex;
    align-items: flex-start;
    gap: 12px;
    padding: 12px 14px;
    border-radius: var(--radius-lg);
    background: var(--glass-bg-strong);
    backdrop-filter: var(--glass-blur-strong);
    -webkit-backdrop-filter: var(--glass-blur-strong);
    border: var(--glass-border);
    box-shadow: var(--shadow-xl);
    color: var(--surface-fg);
    position: relative;
    overflow: hidden;

    &::before {
        content: '';
        position: absolute;
        inset: 0;
        background: linear-gradient(180deg, rgba(255, 255, 255, 0.55), rgba(255, 255, 255, 0) 35%);
        pointer-events: none;
    }
}

.toast__icon {
    flex-shrink: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    border-radius: var(--radius-md);
    color: white;
    background: var(--gradient-brand);
    box-shadow:
        0 4px 8px color-mix(in srgb, var(--color-primary-500) 22%, transparent),
        inset 0 1px 0 rgba(255, 255, 255, 0.18);
    position: relative;
    z-index: 1;
}

.toast--success .toast__icon {
    background: var(--gradient-success);
    box-shadow:
        0 4px 8px rgba(16, 185, 129, 0.32),
        inset 0 1px 0 rgba(255, 255, 255, 0.18);
}

.toast--error .toast__icon {
    background: var(--gradient-danger);
    box-shadow:
        0 4px 8px rgba(244, 63, 94, 0.32),
        inset 0 1px 0 rgba(255, 255, 255, 0.18);
}

.toast--warning .toast__icon {
    background: linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%);
    box-shadow:
        0 4px 8px rgba(245, 158, 11, 0.32),
        inset 0 1px 0 rgba(255, 255, 255, 0.2);
}

.toast__body {
    flex: 1;
    min-width: 0;
    position: relative;
    z-index: 1;
}

.toast__title {
    font-size: 13.5px;
    font-weight: 600;
    color: var(--surface-fg);
    letter-spacing: -0.011em;
    margin-bottom: 2px;
}

.toast__message {
    font-size: 12.5px;
    line-height: 1.4;
    color: var(--surface-muted);
}

.toast__close {
    flex-shrink: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    border-radius: var(--radius-pill);
    color: var(--surface-subtle);
    transition: all var(--motion-fast) var(--ease-spring);
    position: relative;
    z-index: 1;

    &:hover {
        background: rgba(15, 23, 42, 0.06);
        color: var(--surface-fg);
    }

    &:focus-visible {
        outline: none;
        box-shadow: var(--shadow-glow);
    }
}

/* ============ Nouvelle coque : design nu (surface plate + magenta) ============ */
.toast-container--nu {
    top: calc(52px + env(safe-area-inset-top, 0px) + 14px);
}
.toast-container--nu .toast {
    background: var(--nu-surface, #fff);
    backdrop-filter: none;
    -webkit-backdrop-filter: none;
    border: 1px solid var(--nu-line, #e4e7ec);
    box-shadow: var(--nu-shadow, 0 10px 30px rgba(23, 26, 31, 0.12));

    &::before {
        display: none;
    }
}
.toast-container--nu .toast__icon {
    background: var(--nu-magenta, #ec1e78);
    box-shadow: none;
    border-radius: var(--nu-radius-sm, 10px);
}
.toast-container--nu .toast--success .toast__icon {
    background: var(--nu-good, #17936b);
}
.toast-container--nu .toast--error .toast__icon {
    background: #dc2626;
}
.toast-container--nu .toast--warning .toast__icon {
    background: var(--nu-warn, #d9860b);
}
.toast-container--nu .toast__title {
    color: var(--nu-ink, #171a1f);
}
.toast-container--nu .toast__message {
    color: var(--nu-ink-soft, #4a4f58);
}

.toast-enter-active,
.toast-leave-active {
    transition: all 0.35s var(--ease-spring);
}

.toast-enter-from {
    opacity: 0;
    transform: translateY(-12px) scale(0.96);
}

.toast-leave-to {
    opacity: 0;
    transform: translateY(-8px) scale(0.97);
}

.toast-move {
    transition: transform 0.3s var(--ease-spring);
}

@media (prefers-reduced-motion: reduce) {
    .toast-enter-active,
    .toast-leave-active,
    .toast-move {
        transition: opacity 0.15s ease;
    }
    .toast-enter-from,
    .toast-leave-to {
        transform: none;
    }
}
</style>
